import { FEATURES } from '@constants/Text'
import { TOOL_PART_STATE } from '@modules/aawz-intelligence-v2/store/toolParts'

/**
 * Registry PARCIAL do `aawz-intelligence-v2` — spec-001 `plataforma-por-chat`,
 * [T017] / US-2.
 *
 * Os seis cartões que já existiam no chat do CRM/Consultor, escritos como
 * entradas do `UI_REGISTRY`: é isto que tira o `v-if` de cartão de dentro do
 * `chat-message.vue` e faz cartão novo ser UMA entrada num arquivo de módulo.
 *
 * | chave | componente | de onde vem o payload |
 * |---|---|---|
 * | `crm/confirmacao` | casca sobre `ia-confirm-action` | preview das write-tools genéricas |
 * | `crm/servicos-citados` | `servicos-citados` | `citar_servicos` |
 * | `crm/perguntas-em-aberto` | `open-questions` | `perguntar_ao_gestor` **ou** `parecer.perguntas_em_aberto` |
 * | `consultor/painel-deliberacao` | casca sobre `painel-deliberacao` | `convocar_painel` (`tool_calls[].painel`) |
 * | `kernel/erro-de-contrato` | `fallback-card` | `core/ui_payload._cartao_de_erro` (servidor) |
 * | `bi/type` | `dashboard/type-component` | (sem tool emissora nesta spec) |
 * | `documento/preparo-de-reuniao` | `preparo-reuniao-card` (spec-001 `preparacao-reuniao`, [T017]) | atalho `/pré-reunião` (`ui.component` direto) |
 * | `documento/nota-de-reuniao` | `nota-de-reuniao-card` (spec-002 `pos-reuniao-follow-up`, [T033]) | atalho `/pós-reunião` (`ui.component` direto) |
 *
 * ## Três decisões que valem explicar
 *
 * 1. **Duas cascas, e só duas.** `crm/confirmacao` e `consultor/painel-deliberacao`
 *    apontam para um wrapper porque os componentes deles pedem UM objeto
 *    (`preview`, `painel`) e o renderer entrega os props espalhados. As outras
 *    quatro entradas apontam para o componente direto — casca sem trabalho é
 *    indireção pura, e o preço dela é quem lê daqui a um ano.
 * 2. **`schema` é a cópia byte a byte do backend** (`ui-registry/schemas/`, o
 *    [T020] compara). Cópia, e não redigitação: o schema descreve o `value` que
 *    a tool devolve hoje, e um campo reescrito à mão aqui vira cartão reprovado
 *    em produção sem nada ficar vermelho no CI.
 * 3. **`feature` só no `bi/type`.** As outras cinco são o próprio chat: quem
 *    está lendo a conversa já tem o módulo que a desenha. O `bi/type` traz um
 *    componente de OUTRO módulo para dentro da bolha, e aí a flag do escritório
 *    manda — sem o `show`, o registry o recusa (fail-closed).
 *
 * `plataforma/acao-oferecida` NÃO entra aqui: o cartão é do [T015], que registra
 * a própria entrada. Duas tasks registrando a mesma chave se sobrescreveriam —
 * o `registrar` recusa a segunda e o módulo que carregar depois quebra no boot.
 */

// `module` é chave do catálogo de navegação (`ROUTE_KEYS` em
// `@constants/navigation-routes`). Literal aqui de propósito: aquele arquivo
// arrasta uma dúzia de módulos no import, e este registry carrega junto com o
// chat. A igualdade com `ROUTE_KEYS` é aferida no `registry.unit.js`.
const MODULO_CHAT = 'aawz-intelligence'
const MODULO_DASHBOARD = 'dashboard'

// Os cinco `ToolState` do contrato, nomeados pelo papel que têm nas entradas.
const ESQUELETO = TOOL_PART_STATE.ENTRADA
const AGUARDANDO_APROVACAO = TOOL_PART_STATE.APROVACAO
const RESOLVIDO = TOOL_PART_STATE.SAIDA
const NEGADO = TOOL_PART_STATE.RECUSA
const ERRO = TOOL_PART_STATE.ERRO

/**
 * O contrato do `chat/resultado-bruto` — INLINE, e não um arquivo em
 * `ui-registry/schemas/`.
 *
 * ★ Aquele diretório é a cópia BYTE A BYTE de `aawz_intelligence_v2/contratos/`
 * (§13 do CLAUDE.md do módulo), e este cartão **não tem contraparte lá**: ele
 * nasce no FRONT. Quem o monta é `partes.js::parteDoResultadoBruto`, a partir
 * do PONTEIRO de offload (`usecases/agente/_tool_result_grande_apoio.py`) —
 * um evento que sai justamente SEM `ui`. Não há tool emissora, logo não há
 * `UI_SCHEMA`, logo não há arquivo a copiar; um arquivo ali prometeria um
 * espelho que não existe, e a próxima pessoa iria procurá-lo em `contratos/`.
 * Mesmo desenho dos três cartões de `ui-registry/plataforma.js`.
 *
 * ⚠️ `additionalProperties: false` FICA: o `props` é montado aqui do lado,
 * pelo próprio front — campo a mais significa que a montagem mudou e o cartão
 * precisa saber, não que o backend estreou algo.
 *
 * ⚠️ No dia em que o backend publicar o contrato (o conserto de raiz é ele
 * montar o `ui` reduzido no `tool_result` do Modo Agente — `core/ui_payload.
 * _aplicar_teto` já sabe), esta constante SAI e o schema vira cópia byte a
 * byte, junto com a entrada em `SCHEMA_INLINE_PERMITIDO`.
 */
const SCHEMA_DO_RESULTADO_BRUTO = {
  $schema: 'https://json-schema.org/draft/2020-12/schema',
  $id: 'ui-chat-resultado-bruto.schema.json',
  title: 'Resultado bruto de uma tool que não coube no evento',
  type: 'object',
  additionalProperties: false,
  required: ['titulo', 'bytes', 'trecho'],
  properties: {
    // O que produziu o resultado — rótulo da tool, em português.
    titulo: { type: 'string', minLength: 1 },
    // Tamanho REAL do resultado serializado, não o do trecho.
    bytes: { type: 'integer', minimum: 0 },
    // Os primeiros bytes do valor serializado, como vieram — pode estar
    // cortado no meio de uma estrutura, que é o preço de caber.
    trecho: { type: 'string' },
  },
}

export default {
  // O preview de uma escrita: o que vai ser gravado, e os botões que autorizam.
  // Tem os quatro estados porque é o único cartão desta lista que participa do
  // two-step — inclusive o `output-denied`, que é a recusa já registrada.
  'crm/confirmacao': {
    component: () =>
      import(
        '@modules/aawz-intelligence-v2/components/crm-confirmacao-card/crm-confirmacao-card.vue'
      ),
    schema: () =>
      import('@modules/aawz-intelligence-v2/ui-registry/schemas/ui-crm-confirmacao.schema.json'),
    module: MODULO_CHAT,
    states: [ESQUELETO, AGUARDANDO_APROVACAO, RESOLVIDO, NEGADO],
    label: 'Confirmação de gravação',
  },

  // Os "Próximos passos" do Consultor. O rótulo é o título que o cartão já
  // desenha — dois nomes para a mesma coisa é como um vira legado do outro.
  'crm/servicos-citados': {
    component: () =>
      import('@modules/aawz-intelligence-v2/components/servicos-citados/servicos-citados.vue'),
    schema: () =>
      import('@modules/aawz-intelligence-v2/ui-registry/schemas/ui-crm-servicos-citados.schema.json'),
    module: MODULO_CHAT,
    states: [RESOLVIDO],
    label: 'Próximos passos',
  },

  'crm/perguntas-em-aberto': {
    component: () =>
      import('@modules/aawz-intelligence-v2/components/open-questions/open-questions.vue'),
    schema: () =>
      import(
        '@modules/aawz-intelligence-v2/ui-registry/schemas/ui-crm-perguntas-em-aberto.schema.json'
      ),
    module: MODULO_CHAT,
    states: [RESOLVIDO],
    label: 'Perguntas em aberto',
  },

  'consultor/painel-deliberacao': {
    component: () =>
      import(
        '@modules/aawz-intelligence-v2/components/painel-deliberacao-card/painel-deliberacao-card.vue'
      ),
    schema: () =>
      import(
        '@modules/aawz-intelligence-v2/ui-registry/schemas/ui-consultor-painel-deliberacao.schema.json'
      ),
    module: MODULO_CHAT,
    states: [RESOLVIDO],
    label: 'Deliberação do painel',
  },

  // O cartão de contingência do SERVIDOR: `validar_ui` o devolve no lugar de um
  // `ui` que reprovou no contrato. Mesmo componente que o front usa quando a
  // recusa nasce aqui — a diferença é só de onde vem o motivo.
  'kernel/erro-de-contrato': {
    component: () => import('@modules/aawz-intelligence-v2/ui-registry/fallback-card.vue'),
    schema: () =>
      import(
        '@modules/aawz-intelligence-v2/ui-registry/schemas/ui-kernel-erro-de-contrato.schema.json'
      ),
    module: MODULO_CHAT,
    states: [ERRO],
    label: 'Cartão indisponível',
  },

  // Um componente de dashboard dentro da conversa. `type` resolve o arquivo
  // `types/type-{type}.vue` por `require.context`; o enum do schema é o espelho
  // dos 19 arquivos, e tipo fora dele não desenha nada — por isso a recusa vem
  // do contrato, e não do componente.
  // ★ O `feature` desta entrada vale para a CHAVE, não para o componente.
  //
  // `type-component.vue` é desenho reutilizável, e outras entradas podem
  // apontar para ele sem passar por este gate — é o caso, por decisão do
  // owner, da composição financeira do escritório (DRE), que não pode ficar
  // refém de ter contratado o módulo de BI. Portanto: emitir por `bi/type`
  // exige a flag; desenhar um `type-component` NÃO exige. Uma régua que
  // parece proteger o componente e protege só a chave é pior que régua
  // nenhuma, porque alguém confia nela — daí este parágrafo.
  //
  // O gate continua valendo aqui como defesa em profundidade: o backend já
  // não emite este cartão para quem não contratou (a tool nem entra no
  // catálogo do agente), e este `feature` cobre o caso de o backend emitir
  // errado — camada diferente, e vale.
  'bi/type': {
    component: () => import('@modules/dashboard/components/type-component.vue'),
    schema: () =>
      import('@modules/aawz-intelligence-v2/ui-registry/schemas/ui-bi-type.schema.json'),
    module: MODULO_DASHBOARD,
    feature: FEATURES.DASHBOARD,
    states: [RESOLVIDO],
    // À LETRA o `LABEL` de `tool_get_indicador_bi.py` — desde que a parte
    // ganhou tool emissora, o gate `Text.tool-labels.unit.js` compara os dois,
    // e o `tool-call-badge` lê `entrada.label` ANTES do `TOOL_LABELS` (mesma
    // razão escrita em `@modules/atendimento/ia/registry.js`). O título do card
    // de prévia NÃO fica em gerúndio: `tituloDoArtefato()` tira o "Consultando "
    // e o "...", então isto vira "Indicador do BI" na tela.
    //
    // ★ Era 'Componente do dashboard', e a evidência de que o substantivo
    // estava certo parecia forte: as SEIS entradas deste registry usavam
    // substantivo, contra o gerúndio dos `LABEL` do backend. A amostra era
    // enviesada por construção — aquelas seis eram exatamente as entradas SEM
    // tool emissora, isto é, o conjunto sobre o qual este gate nunca teve nada
    // a dizer. Quem TEM emissora (`@modules/atendimento`) já usava gerúndio, e
    // `ui-registry/utils/titulo-do-artefato.js:4-15` declara o desenho por
    // escrito. Fica registrado porque o erro é fácil de repetir: um conjunto
    // cheio de casos irrelevantes disfarça melhor que um conjunto vazio.
    label: 'Consultando indicador do BI...',
  },

  // A composição financeira do panorama do escritório (`get_office_360`).
  //
  // MESMO componente do `bi/type` — o `type-component` é genérico e resolve
  // `types/type-{type}.vue` para qualquer um dos 19 —, mas chave própria e
  // schema próprio.
  //
  // ★ Sem `feature`, de propósito — e isto precisa estar escrito, senão o
  // próximo leitor vê seis entradas sem gate e uma com, "corrige" esta, e
  // reintroduz o acoplamento que ela existe para evitar.
  //
  // 1. A proteção é do BACKEND, campo a campo: o próprio `Valor` carrega o
  //    estado `funcionalidade_nao_contratada`, e é ele que decide o que entra
  //    no cartão. Uma régua só — a fina. Duas, e a grossa esconde a fina no
  //    dia em que divergirem.
  // 2. `feature` é FAIL-CLOSED quando falta o mapa `show`
  //    (`ui-registry/index.js:246`: `show` chega por argumento e, sem ele,
  //    `ctx.show || {}` deixa a entrada ser recusada com `flag_desligada`).
  //    O gate recusaria não só quem não contratou, mas QUALQUER superfície que
  //    não passe o `show`. Medido na US-8: três testes do
  //    `crm-confirmacao-card` quebraram por montar sem store, e a correção foi
  //    tornar o cartão tolerante à ausência dele, porque outras superfícies não
  //    têm o módulo. A composição financeira vai querer aparecer em mais de um
  //    lugar. [achado do `merge-2242`, e é o motivo que quebra na prática]
  // 3. O `feature` de `bi/type` NÃO cobre esta entrada: aquele gate vale para a
  //    CHAVE, não para o `type-component.vue` — está escrito lá em cima.
  //
  // O dado, além disso, é DRE e não BI: gatear por `FEATURES.DASHBOARD` faria
  // um escritório sem o módulo de BI deixar de ver o próprio resultado, com o
  // sintoma de um fallback-card indistinguível de contrato errado.
  // (Decisão do dono do produto, 16/09/2026.)
  'office/composicao-financeira': {
    component: () => import('@modules/dashboard/components/type-component.vue'),
    // Contrato PRÓPRIO, e não o `ui-bi-type`: a chave deriva o nome do arquivo
    // (`contrato-ui-schemas.unit.js:105`) e esta tool produz sempre `bars`, então
    // o schema dela afirma isso em vez de prometer os 19 tipos do componente.
    schema: () =>
      import(
        '@modules/aawz-intelligence-v2/ui-registry/schemas/ui-office-composicao-financeira.schema.json'
      ),
    module: MODULO_CHAT,
    states: [RESOLVIDO],
    // À letra o `LABEL` de `tool_get_office_360.py`, pela mesma razão do
    // `bi/type` acima. Na prévia vira "A visão 360 do escritório".
    label: 'Consultando a visão 360 do escritório...',
  },

  // ★ [T075] O resultado que não coube no evento (offload > 8 KiB) virado
  // ENTREGA no painel. `abrirNoPainelAutomaticamente` porque é exatamente o
  // caso em que o dado NÃO cabe na bolha — deixá-lo inline empurraria a
  // conversa para baixo com um bloco monoespaçado de 8 KiB.
  'chat/resultado-bruto': {
    component: () =>
      import('@modules/aawz-intelligence-v2/ui-registry/cartoes/cartao-resultado-bruto.vue'),
    schema: () => Promise.resolve(SCHEMA_DO_RESULTADO_BRUTO),
    module: MODULO_CHAT,
    states: [RESOLVIDO],
    label: 'Resultado da consulta',
    abrirNoPainelAutomaticamente: true,
  },

  // A pauta de preparação de reunião — spec-001
  // `project-home-ia-skills/preparacao-reuniao`, [T017], D3/D8.3,
  // `contracts/api.md` §4. O atalho `/pré-reunião` emite `ui.component` DIRETO
  // (não passa pelo `LEGACY_TOOL_TO_UI`) — é por isso que este cartão nasce
  // aqui e não depende de mapear um nome de tool.
  //
  // ★ R8 (risco medido em produção por sessão irmã, `plan.md` tabela de
  // riscos): `chaveDaParte()` (`partes-do-turno/partes.js:67-73`) prefere
  // `ui.component` e só cai no mapa legado se ele faltar — como ESTA spec
  // sempre manda `ui.component: 'documento/preparo-de-reuniao'`, a chave é
  // resolvida no PRIMEIRO ramo, sem precisar de entrada no
  // `LEGACY_TOOL_TO_UI`. O nome aqui e o `ui.component` do backend têm de ser
  // BYTE A BYTE o mesmo — `ia/registry.unit.js` testa os DOIS resolvedores
  // (`chaveDaParte` e `resolverEntrada`) com a chave capturada de um payload
  // no formato real, e a mutação "prefixar a chave" tem de dar vermelho.
  //
  // `component` aponta para o EMBRULHO (`preparo-reuniao-card-adaptado.js`,
  // molde de `consolidator/ia/cartoes/posicao-consolidada.js`), não para o
  // `.vue` direto: o payload do fio é snake_case e `id_cliente_app` só vira
  // a prop `idClienteApp` do componente burro através da adaptação — `v-bind`
  // não casa chave por underscore (ver o cabeçalho do embrulho).
  'documento/preparo-de-reuniao': {
    component: () =>
      import(
        '@modules/aawz-intelligence-v2/components/preparo-reuniao-card/preparo-reuniao-card-adaptado'
      ),
    schema: () =>
      import(
        '@modules/aawz-intelligence-v2/ui-registry/schemas/ui-documento-preparo-de-reuniao.schema.json'
      ),
    module: MODULO_CHAT,
    states: [RESOLVIDO],
    label: 'Preparação de reunião',
  },

  // O documento de compliance do cliente — spec-004-compliance-cliente,
  // [T053], `contracts/ui-components.md`. Mesmo caminho dos irmãos acima: o
  // atalho `/compliance` manda `ui.component` DIRETO, sem passar pelo
  // `LEGACY_TOOL_TO_UI`.
  //
  // `component` aponta para o EMBRULHO (`compliance-cliente-card-adaptado.js`,
  // molde de `panorama-card-adaptado.js`), não para o `.vue` direto: o
  // payload do fio é snake_case (`id_cliente_app`, `nome_cliente`) e só vira
  // as props camelCase do componente burro através da adaptação — `v-bind`
  // não casa chave por underscore (ver o cabeçalho do embrulho).
  //
  // Sem `feature`: o gate de "o escritório contratou Compliance" é do
  // SERVIDOR, achado a achado (a régua em `core/compliance_cliente/regua.py`
  // já decide o que entra no documento) — uma flag aqui esconderia o
  // documento inteiro por causa de um módulo, quando o parecer é justamente
  // o que sobra quando parte dele falta (mesmo racional de
  // `documento/panorama` acima).
  'documento/compliance-do-cliente': {
    component: () =>
      import(
        '@modules/aawz-intelligence-v2/components/compliance-cliente-card/compliance-cliente-card-adaptado'
      ),
    schema: () =>
      import(
        '@modules/aawz-intelligence-v2/ui-registry/schemas/ui-documento-compliance-do-cliente.schema.json'
      ),
    module: MODULO_CHAT,
    states: [RESOLVIDO],
    label: 'Compliance do cliente',
  },

  // A nota do PÓS-reunião — spec-002-pos-reuniao-follow-up [T033], US-3.
  // Mesmo caminho dos dois irmãos acima: o atalho manda `ui.component`
  // DIRETO, então a chave resolve no primeiro ramo de `chaveDaParte`, sem
  // entrada no `LEGACY_TOOL_TO_UI` — e `registry.nota-de-reuniao.unit.js`
  // testa os DOIS resolvedores com a chave capturada do payload real, com a
  // mutação "prefixar a chave" dando vermelho.
  //
  // ★ `label` é o `LABEL` de `get_nota_de_reuniao`, BYTE A BYTE: é o que o
  // `tool-call-badge` mostra enquanto a tool roda, e um rótulo divergente não
  // quebra nada visivelmente — o badge diz uma coisa e o cartão diz outra.
  //
  // `component` aponta para o EMBRULHO (`nota-de-reuniao-card-adaptado.js`):
  // o payload do fio é snake_case, e `id_cliente_app` só vira a prop
  // `idClienteApp` através da adaptação — `v-bind` não casa chave por
  // underscore.
  'documento/nota-de-reuniao': {
    component: () =>
      import(
        '@modules/aawz-intelligence-v2/components/nota-de-reuniao-card/nota-de-reuniao-card-adaptado'
      ),
    schema: () =>
      import(
        '@modules/aawz-intelligence-v2/ui-registry/schemas/ui-documento-nota-de-reuniao.schema.json'
      ),
    module: MODULO_CHAT,
    states: [RESOLVIDO],
    label: 'Montando a nota da reunião...',
  },

  // Por quem começar a migração para fee-based — spec-008-quem-migrar-
  // clientes, [T083]. Mesmo caminho dos dois irmãos acima: o atalho
  // `/quem-migrar` manda `ui.component` DIRETO, sem passar pelo
  // `LEGACY_TOOL_TO_UI`.
  //
  // Contrato IDÊNTICO em forma ao de `documento/panorama`
  // (`ui-documento-quem-migrar.schema.json`) — mesmo enum de `resultado`,
  // mesma disciplina de curadoria. A diferença mora só no COMPONENTE: aqui
  // as seções trazem linha em formato de lista, e cada linha ganha checkbox
  // (marcação de tela — nunca escreve, ver `quem-migrar-card.vue`).
  //
  // Sem `feature`, mesmo motivo do panorama: nenhum módulo contratável
  // controla esta faceta — o piso é sugestão, não corte (a ordem por AuC
  // decrescente nunca muda).
  'documento/quem-migrar': {
    component: () =>
      import(
        '@modules/aawz-intelligence-v2/components/quem-migrar-card/quem-migrar-card-adaptado'
      ),
    schema: () =>
      import(
        '@modules/aawz-intelligence-v2/ui-registry/schemas/ui-documento-quem-migrar.schema.json'
      ),
    module: MODULO_CHAT,
    states: [RESOLVIDO],
    // Byte a byte o `LABEL` de `priorizar_carteira_fee_fixo`, a tool emissora
    // (`tool_fee_fixo_carteira.py:78` ↔ `constants/Text.js` `TOOL_LABELS`).
    label: 'Consultando por quem começar a migração...',
  },

  // O documento de revisão de rebalanceamento — spec-003-rebalanceamento-
  // portfolio, [T021], `contracts/ui-components.md` §1. Mesmo caminho dos
  // irmãos acima: o atalho `/rebalance` manda `ui.component` DIRETO, sem
  // passar pelo `LEGACY_TOOL_TO_UI`.
  //
  // `component` aponta para o EMBRULHO (`revisao-rebalanceamento-card-
  // adaptado.js`, molde de `preparo-reuniao-card-adaptado.js`), não para o
  // `.vue` direto: o payload do fio é snake_case (`id_cliente_app`,
  // `nome_cliente`) e só vira as props camelCase do componente burro através
  // da adaptação — `v-bind` não casa chave por underscore.
  //
  // Sem `feature`: nenhum módulo contratável controla esta faceta (é a
  // skill `rebalance-portfolio`, `risk_level='alto'`, gate por permissão de
  // skill — não por `escritorio_funcionalidades`).
  'documento/revisao-de-rebalanceamento': {
    component: () =>
      import(
        '@modules/aawz-intelligence-v2/components/revisao-rebalanceamento-card/revisao-rebalanceamento-card-adaptado'
      ),
    schema: () =>
      import(
        '@modules/aawz-intelligence-v2/ui-registry/schemas/ui-documento-revisao-de-rebalanceamento.schema.json'
      ),
    module: MODULO_CHAT,
    states: [RESOLVIDO],
    label: 'Revisão de rebalanceamento',
  },

  // O documento do `/panorama` — spec-013
  // `atalhos-orquestracao-multimodulo-e-documento` ([T007], US-5). Só dele,
  // hoje: `core/atalhos/fio.py:167` devolve `None` para qualquer tool que não
  // seja `get_office_360`, então o `/pré-reunião` NÃO emite documento — sai
  // com resumo e `tool_result`, sem cartão (e sem erro). Sem tool
  // emissora própria (`Text.tool-labels.unit.js` não acha
  // `ui-documento-panorama.schema.json` em `tools/*.py`: quem monta o `props`
  // é `core/atalhos/documento.py`, chamado pelo ORQUESTRADOR do atalho, não
  // por uma `tool_*.py` — por isso o rótulo abaixo não precisa (nem pode) ser
  // conferido byte a byte contra um `LABEL` de tool).
  //
  // `abrirNoPainelAutomaticamente` é a ÚNICA diferença de contrato desta
  // entrada em relação às outras "cartão de dados" do registry: o documento
  // do panorama não espera clique na prévia (`ArtefatoPreviewCard`) — o
  // `renderiza-partes.js` abre o painel sozinho assim que esta chave resolve
  // com o schema validado (US-5: "o painel abre com o documento"). Nenhum
  // outro cartão liga esta flag hoje — é ela que `montarPreviewDeArtefato`
  // lê para decidir o auto-dispatch, e a ausência dela (o caso comum) mantém
  // cada cartão de dados exigindo o clique de sempre.
  // ★ [merge release/ia-claude-code, 20/09/2026] Esta entrada existia nos
  // DOIS lados do merge, com componente diferente: `cartao-documento.vue`
  // (spec-013 [T007], a spec DONA do `/panorama`) e
  // `components/panorama-card/panorama-card-adaptado` (escrito em 19/09 por
  // uma sessão que mediu o `fallback-card` e não sabia que o cartão já
  // existia). Fica o da spec-013: é ele que `abertura-do-documento.unit.js`
  // exige com `abrirNoPainelAutomaticamente`, e é o mesmo `.vue` do irmão
  // `chat/resultado-bruto` logo acima. O `panorama-card` continua no repo,
  // com teste próprio, mas SEM entrada no registry — se a tela dele for a
  // desejada, trocar o `component` desta entrada é uma linha.
  'documento/panorama': {
    component: () =>
      import('@modules/aawz-intelligence-v2/ui-registry/cartoes/cartao-documento.vue'),
    schema: () =>
      import('@modules/aawz-intelligence-v2/ui-registry/schemas/ui-documento-panorama.schema.json'),
    module: MODULO_CHAT,
    states: [RESOLVIDO],
    label: 'Consultando o panorama do escritório...',
    abrirNoPainelAutomaticamente: true,
  },
}
